import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/base/Alert';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import { cn } from '@/lib/cn';
import { formatCurrency } from '@/lib/format';
import { computeLinesTotal, round2, unitPrice } from '@/lib/pricing';
import { ITEM_SERVICE_TYPES, type LaundryItem, type OrderView, type ReviewLine } from '@/types';

interface ReviewContentsProps {
  order: OrderView;
  items: LaundryItem[];
  currency: string;
  onAdopt: (lines: ReviewLine[]) => Promise<void>;
}

function buildLines(order: OrderView): ReviewLine[] {
  return order.items.map((item) => ({
    key: item.id,
    orderItemId: item.id,
    itemId: item.itemId,
    itemName: item.itemName,
    serviceType: item.serviceType,
    quantity: item.quantity,
    source: 'recorded' as const,
  }));
}

export default function ReviewContents({ order, items, currency, onAdopt }: ReviewContentsProps) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<ReviewLine[]>(() => buildLines(order));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addItemId, setAddItemId] = useState('');
  const [addService, setAddService] = useState('');
  const [addQty, setAddQty] = useState('1');

  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const activeItems = useMemo(() => items.filter((item) => item.active), [items]);
  const recordedById = useMemo(
    () => new Map(order.items.map((item) => [item.id, item])),
    [order.items],
  );

  const original = useMemo(() => computeLinesTotal(order.items, order.priority, byId), [order, byId]);
  const edited = useMemo(() => computeLinesTotal(lines, order.priority, byId), [lines, order.priority, byId]);

  const totalQty = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);
  const diff = round2(edited.total - original.total);
  const finalTotal = round2(order.total + diff);
  const emptyIssue = totalQty <= 0;
  const unavailableIssue = edited.unavailable;
  const hasIssues = emptyIssue || unavailableIssue;

  const itemOptions = activeItems.map((item) => ({ value: item.id, label: item.name }));
  const serviceOptionsFor = (itemId: string) => {
    const item = byId.get(itemId);
    if (!item) return [];
    return ITEM_SERVICE_TYPES.filter((key) => item.services[key].available).map((key) => ({
      value: key,
      label: t(`items.service.${key}`),
    }));
  };

  const updateLine = (key: string, changes: Partial<ReviewLine>) =>
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...changes } : line)));

  const changeItemType = (key: string, itemId: string) => {
    const item = byId.get(itemId);
    updateLine(key, { itemId, itemName: item?.name ?? '' });
  };

  const changeQty = (key: string, raw: string) => {
    const value = Math.max(0, Math.floor(Number(raw) || 0));
    updateLine(key, { quantity: value });
  };

  const openAdd = () => {
    const first = activeItems[0];
    setAddItemId(first?.id ?? '');
    setAddService(serviceOptionsFor(first?.id ?? '')[0]?.value ?? '');
    setAddQty('1');
    setAddOpen(true);
  };

  const onAddItemChange = (itemId: string) => {
    setAddItemId(itemId);
    const options = serviceOptionsFor(itemId);
    setAddService(options[0]?.value ?? '');
  };

  const confirmAdd = () => {
    const item = byId.get(addItemId);
    if (!item || !addService) return;
    const line: ReviewLine = {
      key: `added-${Date.now()}`,
      orderItemId: null,
      itemId: item.id,
      itemName: item.name,
      serviceType: addService as ReviewLine['serviceType'],
      quantity: Math.max(1, Math.floor(Number(addQty) || 1)),
      source: 'added',
    };
    setLines((prev) => [...prev, line]);
    setAddOpen(false);
  };

  const handleAdopt = async () => {
    if (hasIssues) return;
    setSaving(true);
    setFormError(null);
    try {
      await onAdopt(lines);
    } catch {
      setFormError(t('orders.errorAction'));
    } finally {
      setSaving(false);
    }
  };

  const diffTone = diff > 0 ? 'text-rose-600' : diff < 0 ? 'text-emerald-600' : 'text-foreground-700';
  const diffLabel =
    diff > 0 ? t('orders.review.diffIncrease') : diff < 0 ? t('orders.review.diffDecrease') : t('orders.review.diffNone');

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
        <div>
          <h2 className="text-base font-semibold text-foreground-950">{t('orders.review.title')}</h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('orders.review.note')}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon="ri-add-line"
          onClick={openAdd}
          disabled={addOpen || activeItems.length === 0}
        >
          {t('orders.review.addItem')}
        </Button>
      </div>

      <div className="space-y-3 p-4 md:p-5">
        {formError ? <Alert variant="error">{formError}</Alert> : null}
        {hasIssues ? (
          <Alert variant="warning" title={t('orders.review.issue.hint')}>
            <ul className="list-inside list-disc space-y-0.5">
              {unavailableIssue ? <li>{t('orders.review.issue.unavailable')}</li> : null}
              {emptyIssue ? <li>{t('orders.review.issue.empty')}</li> : null}
            </ul>
          </Alert>
        ) : null}

        {lines.map((line) => {
          const recorded = line.source === 'recorded';
          const recordedQty = line.orderItemId ? recordedById.get(line.orderItemId)?.quantity : undefined;
          const lineUnavailable = unitPrice(byId.get(line.itemId), line.serviceType, order.priority) == null;
          return (
            <div
              key={line.key}
              className={cn(
                'grid grid-cols-1 gap-3 rounded-md border p-3.5 md:grid-cols-12 md:items-start',
                recorded ? 'border-background-200 bg-background-50' : 'border-accent-200 bg-accent-100/40',
              )}
            >
              <div className="md:col-span-4">
                <div className="mb-1.5 flex items-center gap-2">
                  <Badge variant={recorded ? 'neutral' : 'accent'}>
                    {t(recorded ? 'orders.review.recordedBadge' : 'orders.review.addedBadge')}
                  </Badge>
                </div>
                <Select
                  id={`item-${line.key}`}
                  label={t('orders.review.actualType')}
                  options={itemOptions}
                  value={line.itemId}
                  onChange={(e) => changeItemType(line.key, e.target.value)}
                />
                {recorded ? (
                  <p className="mt-1 text-xs text-foreground-500">
                    {t('orders.review.recordedType')}: {line.itemName}
                  </p>
                ) : null}
              </div>

              <div className="md:col-span-3">
                {recorded ? (
                  <div>
                    <span className="mb-1.5 block text-sm font-medium text-foreground-800">
                      {t('orders.review.service')}
                    </span>
                    <span className="inline-flex h-11 items-center rounded-md border border-background-200 bg-background-100 px-3.5 text-sm text-foreground-700">
                      {t(`items.service.${line.serviceType}`)}
                    </span>
                    <p className="mt-1 text-xs text-foreground-500">{t('orders.review.serviceLocked')}</p>
                  </div>
                ) : (
                  <Select
                    id={`service-${line.key}`}
                    label={t('orders.review.service')}
                    placeholder={t('orders.review.selectService')}
                    options={serviceOptionsFor(line.itemId)}
                    value={line.serviceType}
                    onChange={(e) => updateLine(line.key, { serviceType: e.target.value as ReviewLine['serviceType'] })}
                  />
                )}
              </div>

              <div className="md:col-span-2">
                <span className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {t('orders.review.recordedQty')}
                </span>
                <span className="flex h-11 items-center text-sm font-semibold text-foreground-800">
                  {recordedQty != null ? recordedQty : '—'}
                </span>
              </div>

              <div className="md:col-span-2">
                <Input
                  id={`qty-${line.key}`}
                  type="number"
                  min={0}
                  label={t('orders.review.actualQty')}
                  value={line.quantity}
                  onChange={(e) => changeQty(line.key, e.target.value)}
                />
              </div>

              <div className="flex items-end md:col-span-1 md:h-full md:justify-end">
                {line.source === 'added' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="ri-delete-bin-line"
                    aria-label={t('orders.review.remove')}
                    onClick={() => setLines((prev) => prev.filter((row) => row.key !== line.key))}
                  />
                ) : null}
              </div>

              {lineUnavailable ? (
                <p className="flex items-center gap-1.5 text-xs text-rose-600 md:col-span-12">
                  <i className="ri-error-warning-line text-base" aria-hidden="true" />
                  {t('orders.review.issue.unavailable')}
                </p>
              ) : null}
            </div>
          );
        })}

        {addOpen ? (
          <div className="rounded-md border border-accent-300 bg-accent-100/50 p-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
              <div className="md:col-span-4">
                <Select
                  id="add-item"
                  label={t('orders.review.selectItem')}
                  placeholder={t('orders.review.selectItem')}
                  options={itemOptions}
                  value={addItemId}
                  onChange={(e) => onAddItemChange(e.target.value)}
                />
              </div>
              <div className="md:col-span-3">
                <Select
                  id="add-service"
                  label={t('orders.review.selectService')}
                  placeholder={t('orders.review.selectService')}
                  options={serviceOptionsFor(addItemId)}
                  value={addService}
                  onChange={(e) => setAddService(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Input
                  id="add-qty"
                  type="number"
                  min={1}
                  label={t('orders.review.actualQty')}
                  value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 md:col-span-3">
                <Button variant="primary" size="md" icon="ri-add-line" onClick={confirmAdd} disabled={!addItemId || !addService}>
                  {t('orders.review.add')}
                </Button>
                <Button variant="outline" size="md" onClick={() => setAddOpen(false)}>
                  {t('orders.review.cancelAdd')}
                </Button>
              </div>
            </div>
            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-accent-900">
              <i className="ri-customer-service-2-line mt-0.5 text-base" aria-hidden="true" />
              {t('orders.review.addItemServiceNote')}
            </p>
          </div>
        ) : null}
      </div>

      <div className="border-t border-background-200 bg-background-100/50 p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-950">{t('orders.review.summary')}</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">{t('orders.review.unitsTotal')}</p>
            <p className="mt-1 text-lg font-bold text-foreground-950">{totalQty}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">{t('orders.review.previousTotal')}</p>
            <p className="mt-1 text-lg font-bold text-foreground-950">{formatCurrency(order.total, currency)}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">{t('orders.review.finalTotal')}</p>
            <p className="mt-1 text-lg font-bold text-foreground-950">{formatCurrency(finalTotal, currency)}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">{t('orders.review.diff')}</p>
            <p className={cn('mt-1 text-lg font-bold', diffTone)}>
              {diffLabel}
              {diff !== 0 ? (
                <span className="ms-1 text-sm">
                  ({diff > 0 ? '+' : '-'}
                  {formatCurrency(Math.abs(diff), currency)})
                </span>
              ) : null}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {hasIssues ? (
            <p className="text-xs text-rose-600">{t('orders.review.issue.hint')}</p>
          ) : null}
          <Button
            variant="primary"
            size="md"
            icon="ri-check-double-line"
            loading={saving}
            disabled={hasIssues || saving}
            onClick={handleAdopt}
          >
            {t('orders.review.adopt')}
          </Button>
        </div>
      </div>
    </section>
  );
}